import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useSettings, CurrencyType, CardDisplayDensity, LanguagePreference } from '../../contexts/SettingsContext';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, updateSettings, resetSettings } = useSettings();
  const { showNotification } = useNotification();

  const isLojista = user?.role_id === 6 || user?.role_id === 1;

  // Lojistas possuem sua própria página de dados e gestão da loja (/minha-loja).
  if (isLojista) {
    return <Navigate to="/minha-loja" replace />;
  }

  // Local state for interactive editing
  const [currency, setCurrency] = useState<CurrencyType>(settings.currency);
  const [usdRate, setUsdRate] = useState<number>(settings.usdToBrlRate);
  const [cardDensity, setCardDensity] = useState<CardDisplayDensity>(settings.cardDensity);
  const [showCardPrices, setShowCardPrices] = useState<boolean>(settings.showCardPrices);
  const [compactSidebar, setCompactSidebar] = useState<boolean>(settings.compactSidebar);
  const [enableSounds, setEnableSounds] = useState<boolean>(settings.enableSounds);
  const [reduceAnimations, setReduceAnimations] = useState<boolean>(settings.reduceAnimations);
  const [language, setLanguage] = useState<LanguagePreference>(settings.language);

  const [savedSuccess, setSavedSuccess] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);

  // Sync state when context changes
  useEffect(() => {
    setCurrency(settings.currency);
    setUsdRate(settings.usdToBrlRate);
    setCardDensity(settings.cardDensity);
    setShowCardPrices(settings.showCardPrices);
    setCompactSidebar(settings.compactSidebar);
    setEnableSounds(settings.enableSounds);
    setReduceAnimations(settings.reduceAnimations);
    setLanguage(settings.language);
  }, [settings]);

  const handleSave = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    updateSettings({
      currency,
      usdToBrlRate: Number(usdRate) > 0 ? Number(usdRate) : 5.0,
      cardDensity,
      showCardPrices,
      compactSidebar,
      enableSounds,
      reduceAnimations,
      language
    });
    setSavedSuccess(true);
    showNotification('Configurações salvas com sucesso!', 'success');
    setTimeout(() => setSavedSuccess(false), 3000);
  };

  const handleResetToDefaults = () => {
    resetSettings();
    setIsResetConfirmOpen(false);
    showNotification('Configurações restauradas para o padrão.', 'info');
  };

  // Preview values
  const sampleCardPriceBrl = 125.5;
  const sampleDeckPriceBrl = 780.0;

  return (
    <div className="max-w-4xl 2xl:max-w-5xl mx-auto pb-24 pt-4 px-2 sm:px-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-black text-white italic tracking-tighter uppercase">
            Configurações
          </h1>
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => setIsResetConfirmOpen(true)}
            className="px-4 py-2 rounded-xl border border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-slate-200 text-xs font-bold transition-colors cursor-pointer flex items-center space-x-2"
          >
            <i className="fas fa-rotate-left text-xs"></i>
            <span>Restaurar Padrões</span>
          </button>
          <button
            type="button"
            onClick={() => handleSave()}
            className="px-6 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold tracking-wider transition-colors flex items-center space-x-2 cursor-pointer"
          >
            <i className="fas fa-check"></i>
            <span>Salvar Alterações</span>
          </button>
        </div>
      </div>

      {savedSuccess && (
        <div className="mb-8 p-4 rounded-2xl bg-emerald-950/40 border border-emerald-500/30 text-emerald-400 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <i className="fas fa-circle-check text-lg"></i>
            <span className="text-sm font-bold">Suas configurações foram salvas e aplicadas em todo o site!</span>
          </div>
          <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 px-2.5 py-1 rounded-full">
            Ativo
          </span>
        </div>
      )}

      {/* Formulário corrido sem abas */}
      <form onSubmit={handleSave} className="space-y-8">
        {/* SEÇÃO 1: MOEDA E PREÇOS */}
        <section className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6">
          <div>
            <h2 className="text-lg sm:text-2xl font-black text-white uppercase tracking-tight flex items-center space-x-2.5">
              <span>Exibição dos Preços</span>
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-2xl">
              Defina se você prefere visualizar os preços estimados de cartas e agregados em{' '}
              <strong className="text-white">Reais (R$)</strong> ou em{' '}
              <strong className="text-white">Dólar ($ USD)</strong>.
            </p>
          </div>

          {/* Opções de Moeda */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Opção BRL */}
            <div
              onClick={() => setCurrency('BRL')}
              className={`p-5 rounded-2xl border-2 cursor-pointer transition-colors relative ${
                currency === 'BRL'
                  ? 'bg-slate-950 border-emerald-500'
                  : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-11 h-11 rounded-2xl flex items-center justify-center font-black text-base ${
                      currency === 'BRL'
                        ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-500/30'
                        : 'bg-slate-800 text-slate-400'
                    }`}
                  >
                    R$
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-base font-black text-white">Real Brasileiro (BRL)</h4>
                    <p className="text-xs text-slate-400">Exibir valores em R$</p>
                  </div>
                </div>
                {currency === 'BRL' && (
                  <span className="w-6 h-6 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center text-xs font-bold">
                    <i className="fas fa-check"></i>
                  </span>
                )}
              </div>

              <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
                <span className="text-slate-500">Exemplo formatado:</span>
                <span className="font-mono font-bold text-slate-200">R$ 125,50</span>
              </div>
            </div>

            {/* Opção USD */}
            <div
              onClick={() => setCurrency('USD')}
              className={`p-5 rounded-2xl border-2 cursor-pointer transition-colors relative ${
                currency === 'USD'
                  ? 'bg-slate-950 border-emerald-500'
                  : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-11 h-11 rounded-2xl flex items-center justify-center font-black text-base ${
                      currency === 'USD'
                        ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-500/30'
                        : 'bg-slate-800 text-slate-400'
                    }`}
                  >
                    $
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-base font-black text-white">Dólar Americano (USD)</h4>
                    <p className="text-xs text-slate-400">Exibir valores em $ (USD)</p>
                  </div>
                </div>
                {currency === 'USD' && (
                  <span className="w-6 h-6 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center text-xs font-bold">
                    <i className="fas fa-check"></i>
                  </span>
                )}
              </div>

              <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
                <span className="text-slate-500">Exemplo formatado:</span>
                <span className="font-mono font-bold text-slate-200">$ 25.10</span>
              </div>
            </div>
          </div>

          {/* Taxa de Conversão */}
          <div className="p-4 sm:p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="space-y-0.5">
                <div className="flex items-center space-x-2">
                  <h4 className="text-sm font-bold text-white">Taxa de Conversão de Referência</h4>
                  <span className="text-[10px] bg-purple-950/40 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded-full font-mono">
                    1 USD = R$ {usdRate.toFixed(2).replace('.', ',')}
                  </span>
                </div>
                <p className="text-xs text-slate-400">
                  Taxa usada para converter os valores entre o catálogo nacional e as bases internacionais.
                </p>
              </div>

              <div className="flex items-center space-x-2 shrink-0">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-bold">R$</span>
                  <input
                    type="number"
                    step="0.05"
                    min="1.0"
                    max="20.0"
                    value={usdRate}
                    onChange={(e) => setUsdRate(Math.max(1, parseFloat(e.target.value) || 5.0))}
                    className="bg-slate-900 border border-slate-700 rounded-xl pl-9 pr-3 py-2 text-white font-mono text-xs w-28 focus:outline-none focus:border-purple-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setUsdRate(5.0)}
                  className="px-3 py-2 bg-slate-800 hover:bg-slate-750 text-slate-300 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                >
                  Padrão (5.00)
                </button>
              </div>
            </div>
          </div>

          {/* Demonstração em Tempo Real */}
          <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-black uppercase tracking-wider text-purple-400 flex items-center space-x-2">
                <i className="fas fa-eye text-sm"></i>
                <span>Pré-visualização de Cálculo</span>
              </h4>
              <span className="text-[10px] font-mono text-slate-400">
                Moeda ativa: <strong className="text-white">{currency}</strong>
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div className="bg-slate-900/80 p-3.5 rounded-xl border border-slate-800 space-y-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Carta Individual
                </p>
                <div className="flex items-baseline space-x-2">
                  <span className="text-xl font-black text-emerald-400 font-mono">
                    {currency === 'USD'
                      ? `$ ${(sampleCardPriceBrl / usdRate).toFixed(2)}`
                      : `R$ ${sampleCardPriceBrl.toFixed(2).replace('.', ',')}`}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    ({currency === 'USD' ? `R$ ${sampleCardPriceBrl.toFixed(2).replace('.', ',')}` : `$ ${(sampleCardPriceBrl / usdRate).toFixed(2)}`})
                  </span>
                </div>
              </div>

              <div className="bg-slate-900/80 p-3.5 rounded-xl border border-slate-800 space-y-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Pasta / Deck Completo
                </p>
                <div className="flex items-baseline space-x-2">
                  <span className="text-xl font-black text-emerald-400 font-mono">
                    {currency === 'USD'
                      ? `$ ${(sampleDeckPriceBrl / usdRate).toFixed(2)}`
                      : `R$ ${sampleDeckPriceBrl.toFixed(2).replace('.', ',')}`}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    ({currency === 'USD' ? `R$ ${sampleDeckPriceBrl.toFixed(2).replace('.', ',')}` : `$ ${(sampleDeckPriceBrl / usdRate).toFixed(2)}`})
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Ocultar / Exibir Preços */}
          <div className="flex items-center justify-between p-4 rounded-2xl bg-slate-950 border border-slate-800">
            <div className="space-y-0.5 pr-4">
              <p className="text-sm font-bold text-white">Exibir preços estimados nas listas e coleções</p>
              <p className="text-xs text-slate-500">
                Caso desativado, os valores estimados de mercado das cartas ficarão ocultos em toda a interface
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowCardPrices(!showCardPrices)}
              className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer shrink-0 ${
                showCardPrices ? 'bg-purple-600' : 'bg-slate-800'
              }`}
            >
              <div
                className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${
                  showCardPrices ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </section>

        {/* SEÇÃO 2: CONTA & DADOS PESSOAIS DO MEMBRO */}
        {user && (
          <section className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6">
            <div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight flex items-center space-x-2.5">
                <i className="fas fa-user-shield text-purple-400 text-sm"></i>
                <span>Conta & Dados</span>
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Atalhos diretos para gerenciar seus dados cadastrais e endereços
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/perfil/editar"
                className="flex items-center justify-between p-4 rounded-2xl bg-slate-950 border border-slate-800 hover:border-purple-500/50 hover:bg-slate-900 transition-colors group"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-950/60 border border-purple-500/20 text-purple-400 flex items-center justify-center text-base">
                    <i className="fas fa-id-card"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white group-hover:text-purple-300 transition-colors">
                      Editar Perfil
                    </h4>
                    <p className="text-[11px] text-slate-500">Nome, bio, fotos e tags</p>
                  </div>
                </div>
                <i className="fas fa-chevron-right text-slate-600 group-hover:text-purple-400 text-xs transition-colors"></i>
              </Link>

              <Link
                to="/perfil/enderecos"
                className="flex items-center justify-between p-4 rounded-2xl bg-slate-950 border border-slate-800 hover:border-emerald-500/50 hover:bg-slate-900 transition-colors group"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-950/60 border border-emerald-500/20 text-emerald-400 flex items-center justify-center text-base">
                    <i className="fas fa-location-dot"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white group-hover:text-emerald-300 transition-colors">
                      Meus Endereços
                    </h4>
                    <p className="text-[11px] text-slate-500">Endereços de entrega cadastrados</p>
                  </div>
                </div>
                <i className="fas fa-chevron-right text-slate-600 group-hover:text-emerald-400 text-xs transition-colors"></i>
              </Link>
            </div>
          </section>
        )}
      </form>

      {/* Modal de Confirmação de Restauração */}
      {isResetConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-950/60 border border-amber-500/20 text-amber-400 flex items-center justify-center text-xl shrink-0">
                <i className="fas fa-triangle-exclamation"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Restaurar Configurações?</h3>
                <p className="text-xs text-slate-400">Esta ação voltará as preferências para a definição padrão.</p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              A moeda voltará para <strong>Real Brasileiro (R$)</strong> e as densidades de cartas voltarão ao modo padrão.
            </p>

            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setIsResetConfirmOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleResetToDefaults}
                className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-colors cursor-pointer"
              >
                Confirmar Restauração
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
